import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { SelectableLot } from '../types/Lot';
import useStores from 'utils/useStores';
import LotItem from './LotItem';

export interface LotTableProps extends React.HTMLAttributes<HTMLTableElement> {
  lots: Array<SelectableLot>;
  profiles?: Array<{ id: string; name: string }>;
}

// Define the Profile interface
interface Profile {
  id: string;
  name: string;
}

const LotTable: React.FC<LotTableProps> = ({ lots, ...props }) => {
  const { setStore } = useStores();

  // Define state for profiles and selectedProfile
  const [profiles, setProfiles] = useState<Profile[]>(props.profiles || []);
  const [selectedProfile, setSelectedProfile] = useState('');

  // Fetch profiles on component mount if not provided by parent
  useEffect(() => {
    if (props.profiles && props.profiles.length > 0) return;

    fetch('/api/v1/profiles')
      .then((response) => response.json())
      .then((data) => {
        setProfiles(data);
      });
  }, [props.profiles]);

  // Handle profile change
  const handleProfileChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const profileId = event.target.value;
    setSelectedProfile(profileId);
    setStore.filterByProfile(profileId);
  };

  const filteredLots = selectedProfile
    ? lots.filter((lot) => String(lot.attributes.profile_id) === String(selectedProfile))
    : lots;

  return (
    <div>
      <div className='filter-container'>
        <label htmlFor='profile-filter'>
          {I18n.translate('lot_sets.index.profile', {
            defaultValue: 'Profile',
          })}
        </label>
        <select
          id='profile-filter'
          onChange={handleProfileChange}
          value={selectedProfile}
        >
          <option value=''>
            {I18n.translate('lot_sets.index.all_profiles', {
              defaultValue: 'All Profiles',
            })}
          </option>
          {profiles.map((profile) => (
            <option key={profile.id} value={profile.id}>
              {profile.name}
            </option>
          ))}
        </select>
      </div>
      <table {...props}>
        <thead>
          <tr>
            <th className='align-left'>ID</th>
            <th className='sets-table__column--sensorial table-border align-left'></th>
            <th className='table-border'>{I18n.translate('lot_sets.index.quality')}</th>
            <th className='table-border'>
              {I18n.translate('lot_sets.index.final_score')}
            </th>
            <th className='sets-table__column--weight table-border'>
              {I18n.translate('lot_sets.index.total')}
            </th>
            <th className='table-border align-left'>
              {I18n.translate('lot_sets.index.producer')}
            </th>
            <th>{I18n.translate('lot_sets.index.add_to_set')}</th>
          </tr>
        </thead>
        <tbody>
          {filteredLots.map((lot) => {
            // Existing validations from selectedRequirements (quality, certification, eudr)
            const requirementDisabled = Object.entries(
              setStore.selectedRequirements
            ).some(([key, value]) => lot.attributes[key] !== value);

            // If there's a locked profile (from previously selected lots in the store),
            // disable lots that have a different profile_id or a null profile_id.
            const lockedProfileId = setStore.selectedLots[0]?.attributes.profile_id
              ? String(setStore.selectedLots[0].attributes.profile_id)
              : null;

            const profileMismatch = lockedProfileId
              ? String(lot.attributes.profile_id) !== lockedProfileId
              : false;

            const disabled = requirementDisabled || profileMismatch;

            const onLotCheck = () => {
              // Prevent selecting disabled lots
              if (disabled) return;

              if (!lot.selected) {
                setStore.selectLot(lot);
              } else {
                setStore.deselectLot(lot);
              }
            };

            return (
              <LotItem
                key={lot.attributes.id}
                lot={lot}
                checked={lot.selected}
                disabled={disabled}
                onCheck={onLotCheck}
              >
                <span style={{ color: 'gray' }}>
                  {profiles.find(
                    (profile) => String(profile.id) === String(lot.attributes.profile_id)
                  )?.name || ''}
                </span>
              </LotItem>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default observer(LotTable);
